import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const fmtQar = (v) => `QAR ${Number(v || 0).toLocaleString()}`;

export const KpiMatrixPanel = ({ employeeId }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [manual, setManual] = useState({});
  const [busy, setBusy] = useState(false);
  const canEdit = ["admin", "hr_manager", "dept_head"].includes(user.role);

  const load = () => api.get(`/kpi/employees/${employeeId}`)
    .then((r) => { setData(r.data); setMissing(false); })
    .catch((e) => { if (e.response?.status === 404) setMissing(true); else setMissing("denied"); });
  useEffect(() => { load(); }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing === true) {
    return <div className="bg-white border border-border px-4 py-10 text-center text-[13px] text-muted-foreground"
      data-testid="kpi-matrix-missing">No KPI matrix instance yet — activate a role matrix that maps to this employee.</div>;
  }
  if (missing === "denied") {
    return <div className="bg-white border border-border px-4 py-10 text-center text-[13px] text-muted-foreground"
      data-testid="kpi-matrix-denied">You do not have access to this employee's KPI matrix.</div>;
  }
  if (!data) return <Skeleton className="h-80 w-full rounded-none" />;

  const saveManual = async () => {
    setBusy(true);
    try {
      const values = Object.fromEntries(Object.entries(manual).filter(([, v]) => v !== ""));
      await api.patch(`/kpi/employees/${employeeId}`, { manual_values: values, note: "manual KPI entry" });
      toast.success("Manual KPI values recorded (audited)");
      setManual({});
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="kpi-matrix-panel">
      <div className="flex items-stretch gap-3 flex-wrap">
        <div className="bg-[var(--brand-primary)] text-white px-5 py-4 flex items-center gap-4">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Weighted KPI score</p>
            <p className="text-3xl font-bold font-mono text-teal-400" data-testid="weighted-score">{data.weighted_score}</p>
          </div>
          <div className="border-l border-white/15 pl-4">
            <p className="text-[10px] font-mono text-slate-300">{data.role_title} matrix</p>
            <p className="text-[10px] font-mono text-slate-400">level {data.level} · target factor ×{data.target_factor}</p>
          </div>
        </div>
        {data.increment && (
          <div className="bg-white border border-border px-5 py-4" data-testid="increment-card">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3 text-teal-600" /> Increment recommendation
            </p>
            <p className="text-xl font-bold font-mono text-teal-700 mt-1">+{data.increment.recommended_pct}%</p>
            {"monthly_increase_qar" in data.increment ? (
              <p className="text-[11px] font-mono text-slate-500">
                {fmtQar(data.increment.monthly_increase_qar)}/mo · {fmtQar(data.increment.annual_cost_qar)}/yr
              </p>
            ) : (
              <p className="text-[10px] text-muted-foreground">QAR impact restricted to HR/admin</p>
            )}
            {data.increment.development_plan && (
              <p className="text-[10px] font-mono text-amber-700 mt-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> DEVELOPMENT PLAN FLAGGED
              </p>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-border divide-y divide-border">
        {data.kpis.map((k) => (
          <div key={k.key} className="px-4 py-3" data-testid={`kpi-row-${k.key}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-[13px] font-medium text-[var(--brand-primary)] flex-1 min-w-[200px]">{k.name}</p>
              <span className="text-[10px] font-mono border border-slate-300 text-slate-500 px-1.5 py-0.5">{k.source}</span>
              <span className="text-[11px] font-mono text-slate-500">weight {k.weight_pct}%</span>
              <span className="text-[11px] font-mono text-slate-600">
                {k.value} / {k.target} {k.unit}
              </span>
              <span className={`text-[12px] font-mono font-semibold ${k.below_threshold ? "text-red-600" : "text-teal-700"}`}>
                {k.attainment_pct}%
              </span>
              {canEdit && k.source === "manual" && (
                <Input type="number" placeholder={`enter (${k.unit})`} data-testid={`manual-input-${k.key}`}
                  value={manual[k.key] ?? ""} onChange={(e) => setManual({ ...manual, [k.key]: e.target.value })}
                  className="rounded-none w-28 h-7 text-[11px] font-mono" />
              )}
            </div>
            <div className="h-1.5 bg-slate-100 mt-2">
              <div className={`h-1.5 ${k.below_threshold ? "bg-amber-500" : "bg-teal-600"}`}
                style={{ width: `${Math.min(100, k.attainment_pct)}%` }} />
            </div>
            {k.source === "manual" && k.manual_entered_by && (
              <p className="text-[10px] font-mono text-slate-400 mt-1">entered by: {k.manual_entered_by}</p>
            )}
          </div>
        ))}
      </div>

      {canEdit && Object.values(manual).some((v) => v !== "") && (
        <div className="flex justify-end">
          <Button onClick={saveManual} disabled={busy} data-testid="manual-save-btn"
            className="rounded-none h-8 text-[12px] bg-teal-600 hover:bg-teal-700">
            {busy ? "Saving…" : "Save manual values"}
          </Button>
        </div>
      )}

      {(data.adjustments || []).length > 0 && (
        <div className="bg-white border border-border px-4 py-3" data-testid="kpi-adjustments">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">Adjustment audit trail</p>
          {data.adjustments.slice(-4).map((a, i) => (
            <p key={i} className="text-[11px] font-mono text-slate-500 py-0.5">
              {String(a.at).slice(0, 16).replace("T", " ")} · {a.by} ({a.role}) · {a.changes.join("; ")}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default KpiMatrixPanel;
